export type CalendarWindowRow = Record<string, unknown> & { id?: unknown };

const rowId = (row: CalendarWindowRow): string | null => {
  if (typeof row.id === "string" && row.id.length > 0) return row.id;
  if (typeof row.id === "number") return String(row.id);
  return null;
};

export const isEphemeralCalendarRowId = (id: string): boolean =>
  id.startsWith("temp") || id.startsWith("draft-");

const readPath = (row: CalendarWindowRow, path: string): unknown => {
  if (!path.includes(".")) return row[path];
  let current: unknown = row;
  for (const part of path.split(".")) {
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return current;
};

const parseInstantMs = (value: unknown): number | null => {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof value === "string" || typeof value === "number") {
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (Array.isArray(value) && value.length === 1) {
    return parseInstantMs(value[0]);
  }
  if (value && typeof value === "object" && "id" in value) {
    return null;
  }
  return null;
};

export const rowOverlapsCalendarWindow = (input: {
  row: CalendarWindowRow;
  startHandle?: string;
  endHandle?: string;
  startAt?: string;
  endAt?: string;
}): boolean => {
  if (!input.startHandle || !input.startAt || !input.endAt) {
    return true;
  }
  const windowStart = Date.parse(input.startAt);
  const windowEnd = Date.parse(input.endAt);
  if (Number.isNaN(windowStart) || Number.isNaN(windowEnd)) {
    return true;
  }
  const startMs = parseInstantMs(readPath(input.row, input.startHandle));
  if (startMs == null) {
    return true;
  }
  const endMs =
    input.endHandle != null
      ? parseInstantMs(readPath(input.row, input.endHandle))
      : null;
  const rowEnd = endMs ?? startMs;
  return startMs <= windowEnd && rowEnd >= windowStart;
};

const isDirty = (
  working: CalendarWindowRow | undefined,
  original: CalendarWindowRow | undefined,
): boolean => {
  if (!working || !original) return false;
  return JSON.stringify(working) !== JSON.stringify(original);
};

/**
 * Merge a calendar window refetch with the rows already on screen.
 * Keeps drafts, in-progress drag edits, and in-window CRM rows the server
 * omitted (stale calendar FK after Google pull, create/update races).
 */
export const mergeCalendarWindowRows = (input: {
  previousWorking: CalendarWindowRow[];
  previousOriginal: CalendarWindowRow[];
  serverRows: CalendarWindowRow[];
  startHandle?: string;
  endHandle?: string;
  startAt?: string;
  endAt?: string;
  alwaysKeepIds?: string[];
}): { working: CalendarWindowRow[]; original: CalendarWindowRow[] } => {
  const prevWorkingById = new Map<string, CalendarWindowRow>();
  const prevOriginalById = new Map<string, CalendarWindowRow>();
  for (const row of input.previousWorking) {
    const id = rowId(row);
    if (id) prevWorkingById.set(id, row);
  }
  for (const row of input.previousOriginal) {
    const id = rowId(row);
    if (id) prevOriginalById.set(id, row);
  }

  const alwaysKeepIds = new Set(
    (input.alwaysKeepIds ?? []).filter((id) => id.length > 0),
  );
  const working: CalendarWindowRow[] = [];
  const original: CalendarWindowRow[] = [];
  const seen = new Set<string>();

  for (const server of input.serverRows) {
    const id = rowId(server);
    if (id) seen.add(id);
    const prevWorking = id ? prevWorkingById.get(id) : undefined;
    const prevOriginal = id ? prevOriginalById.get(id) : undefined;
    working.push(isDirty(prevWorking, prevOriginal) ? prevWorking! : server);
    original.push(server);
  }

  for (const prev of input.previousWorking) {
    const id = rowId(prev);
    if (!id || seen.has(id)) continue;
    if (isEphemeralCalendarRowId(id)) {
      working.push(prev);
      continue;
    }
    if (alwaysKeepIds.has(id)) {
      working.push(prev);
      original.push(prevOriginalById.get(id) ?? prev);
      seen.add(id);
      continue;
    }
    if (
      !rowOverlapsCalendarWindow({
        row: prev,
        startHandle: input.startHandle,
        endHandle: input.endHandle,
        startAt: input.startAt,
        endAt: input.endAt,
      })
    ) {
      continue;
    }
    working.push(prev);
    original.push(prevOriginalById.get(id) ?? prev);
    seen.add(id);
  }

  return { working, original };
};
